import { defineStore } from 'pinia';
import { useUserStore } from './user';

export const useLeadSaleStore = defineStore('leadSale', () => {
    const userStore = useUserStore();

    const loading = ref(true);

    const error = ref(null);

    const isMoveproEnabled = computed(() => Boolean(userStore.data?.company?.movepro));

    const companyId = computed(() => userStore.companyId ?? userStore.getCompanyId ?? null);

    const leadSale = ref(null);

    const order = computed(() => leadSale.value?.order ?? null);

    const move = computed(() => leadSale.value?.order?.move ?? null);

    const inventory = computed(() => leadSale.value?.order?.move?.inventory ?? null);

    const routeData = computed(() => move.value?.route ?? {});

    const pickupAddressDisplay = computed(() => {
        return (
            leadSale.value?.order?.move?.pickup_address_formatted ??
            routeData.value?.start?.formatted ??
            formatLocParts(routeData.value?.start) ??
            '—'
        );
    });

    const deliveryAddressDisplay = computed(() => {
        return (
            leadSale.value?.order?.move?.delivery_address_formatted ??
            routeData.value?.end?.formatted ??
            formatLocParts(routeData.value?.end) ??
            '—'
        );
    });

    function formatLocParts(obj) {
        if (!obj) return '—';
        if (obj.formatted) return obj.formatted;
        const parts = [obj.locality, obj.state, obj.postal_code].filter(Boolean);
        return parts.length ? parts.join(' ') : '—';
    }

    const leadEndpoint = computed(() => {
        const base = import.meta.env.VITE_API_BASE_URL || '';
        const path = `companies/${companyId.value}/leads`;
        return base.includes('/api/v1') ? path : `api/v1/${path}`;
    });

    const getMoveAccessFormatted = (access) => {
        if (access && access.length > 0) {
            return access.map((item) => {
                return {
                    name: item.accessTag.item,
                    unit: item.accessTag.unit,
                    access_tag_id: item.accessTag.id,
                    ...item?.accessTag,
                    value: item.value ?? null,
                };
            });
        } else {
            return [];
        }
    };

    async function retrieve(uuid) {
        const url = leadEndpoint.value + `/${uuid}`;
        if (!companyId.value) {
            throw new Error('Missing company id.');
        }

        if (!uuid) {
            throw new Error('Missing lead uuid.');
        }

        try {
            const res = await window.axios.get(url);
            leadSale.value = res?.data?.data ?? null;
        } catch (e) {
            throw new Error(e?.message || 'Failed to load lead details');
        }
    }

    async function updateLeadSaleStatus(uuid, newStatus) {
        if (!uuid || !newStatus || newStatus === leadSale.value?.status) return;
        error.value = null;
        const url = `${leadEndpoint.value}/${uuid}`;
        try {
            const res = await window.axios.patch(url, {
                status: newStatus,
            });
            leadSale.value = res?.data?.data ?? leadSale.value;
            return res;
        } catch (e) {
            error.value = e?.message || 'Failed to update lead status';
            throw new Error(e?.message || 'Failed to update lead status');
        }
    }

    return {
        retrieve,
        updateLeadSaleStatus,

        loading,
        error,
        isMoveproEnabled,
        leadSale,
        order,
        move,
        inventory,
        routeData,
        pickupAddressDisplay,
        deliveryAddressDisplay,
        leadEndpoint,

        getMoveAccessFormatted,
    };
});
